import React, { useEffect, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useClaims } from '@/v2/hooks/insurance/useClaims'
import Button from '@/v2/components/common/Button'
import useModals from '@/v2/hooks/useModals'
import Box from '@mui/material/Box'
import ClaimInfo from './ClaimInfo'
import RTClaims from '@/v2/hooks/subscriptions/messages/RTClaims'

const ClaimsTab = () => {
  const { id } = useParams()
  const location = useLocation()
  const { goTo } = useModals()
  const { patient } = usePatientContext()
  const { claims, isLoading, isError } = useClaims(id)
  const { onOpenSnackbar } = useSnackbarContext()
  // Tracks which claim we've already auto-opened. We key on the openClaimId
  // VALUE (a number), not on location.state's object identity: React Router
  // reads location.state back from window.history.state, which the browser
  // structured-clones on every navigation, so the object reference changes each
  // time `goTo` navigates. An identity check therefore never matches and the
  // effect re-fires forever (open -> navigate -> new state object -> open ...).
  // The openClaimId value survives cloning, so this guard is stable.
  const openedClaimIdRef = useRef(null)

  useEffect(() => {
    if (!isLoading && !!isError)
      onOpenSnackbar("It wasn't possible to load the patient claims.", 'error')
  }, [isLoading, isError, onOpenSnackbar])

  useEffect(() => {
    const openClaimId = location.state?.openClaimId

    if (
      !isLoading &&
      claims &&
      openClaimId &&
      openedClaimIdRef.current !== openClaimId
    ) {
      const claim = location.state.claim || claims.find(c => c.id === openClaimId)

      if (claim) {
        // Mark this claim handled so the effect doesn't re-open it when
        // location.state is re-cloned by navigation or `claims` is refetched.
        openedClaimIdRef.current = openClaimId
        const status = claim.status

        if (
          status === 'pending' ||
          status === 'invalidated' ||
          status === 'answered'
        ) {
          goTo(`/v2/patient/${patient.id}/claim/edit`, {
            modalType: 'edit',
            patientId: patient.id,
            isEdit: true,
            insuranceId: claim.insurance?.id,
            claim,
            patient: location.state.patient || patient,
            scrollToHistory: location.state.scrollToHistory || false,
          })
        } else if (
          status === 'validated' ||
          status === 'submitted' ||
          status === 'approved' ||
          status === 'cancelled' ||
          status === 'failed' ||
          status === 'denied'
        ) {
          goTo(`/v2/patient/${patient.id}/claim/view`, {
            modalType: 'view',
            patientId: patient.id,
            claimId: claim.id,
            insuranceId: claim.insurance?.id,
            claim,
            scrollToHistory: location.state.scrollToHistory || false,
          })
        }
      }
    }
  }, [isLoading, claims, location.state, patient, goTo])

  if (isLoading)
    return (
      <Typography
        variant="h5"
        fontWeight="300"
        sx={{ textAlign: 'center', marginTop: '40px' }}
      >
        Loading...
      </Typography>
    )

  return (
    <>
      <RTClaims />
      <Box display={'flex'} flexDirection={'row-reverse'}>
        <Button
          variant="text"
          onClick={() =>
            goTo(`v2/patient/${patient.id}/claim/view`, {
              modalType: 'create',
              patientId: patient.id,
              patient,
            })
          }
        >
          <Typography variant="h5">Create Claim</Typography>
        </Button>
      </Box>
      {claims.map(item => (
        <ClaimInfo key={item.id} claim={item} patient={patient} />
      ))}
      {claims.length === 0 && (
        <Typography
          variant="h5"
          fontWeight="300"
          sx={{ textAlign: 'center', marginTop: '40px' }}
        >
          No claims found
        </Typography>
      )}
    </>
  )
}

export default ClaimsTab
